import { alpha, createTheme, type ThemeOptions } from '@mui/material/styles';

import { colors } from './theme';

declare module '@mui/material/styles' {
  interface TypeBackground {
    subtle: string;
    hover: string;
  }
  interface PaletteColor {
    bg: string;
    border: string;
  }
  interface SimplePaletteColorOptions {
    bg?: string;
    border?: string;
  }
}

// Common theme options shared between light and dark
const commonOptions: ThemeOptions = {
  typography: {
    fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
    h1: { fontWeight: 700, letterSpacing: '-0.02em' },
    h2: { fontWeight: 700, letterSpacing: '-0.01em' },
    h3: { fontWeight: 700 },
    h4: { fontWeight: 700 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600 },
    button: { fontWeight: 600, textTransform: 'none' },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          padding: '10px 14px',
          fontWeight: 700,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        containedPrimary: {
          background: colors.accent.blue[600],
          boxShadow: ({ theme }) => `0 4px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
          '&:hover': {
            background: colors.accent.blue[700],
            boxShadow: ({ theme }) => `0 6px 20px ${alpha(theme.palette.primary.main, 0.5)}`,
            transform: 'translateY(-2px)',
          },
        },
        containedSuccess: {
          background: `linear-gradient(135deg, ${colors.accent.green[500]}, ${colors.accent.green[600]})`,
          boxShadow: ({ theme }) => `0 4px 12px ${alpha(theme.palette.success.main, 0.35)}`,
          '&:hover': {
            background: `linear-gradient(135deg, ${colors.accent.green[600]}, ${colors.accent.green[700]})`,
            boxShadow: ({ theme }) => `0 6px 16px ${alpha(theme.palette.success.main, 0.45)}`,
          },
        },
        containedError: {
          background: `linear-gradient(135deg, ${colors.accent.red[500]}, ${colors.accent.red[600]})`,
          boxShadow: ({ theme }) => `0 4px 12px ${alpha(theme.palette.error.main, 0.35)}`,
          '&:hover': {
            background: `linear-gradient(135deg, ${colors.accent.red[600]}, ${colors.accent.red[700]})`,
            boxShadow: ({ theme }) => `0 6px 16px ${alpha(theme.palette.error.main, 0.45)}`,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        // @ts-expect-error - Complex theme callback mismatch
        root: {
          borderRadius: 18,
          boxShadow: (theme: any) =>
            theme.palette.mode === 'dark'
              ? `0 4px 20px ${alpha(theme.palette.common.black, 0.4)}`
              : `0 4px 20px ${alpha(theme.palette.text.primary, 0.08)}`,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          fontWeight: 600,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: 12,
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
  },
};

// Light theme
export const lightTheme = createTheme({
  ...commonOptions,
  palette: {
    mode: 'light',
    primary: {
      main: colors.accent.blue[600],
      light: colors.accent.blue[400],
      dark: colors.accent.blue[700],
      contrastText: '#ffffff',
    },
    secondary: {
      main: colors.accent.purple[700],
      light: colors.accent.purple[400],
      dark: colors.accent.purple[800],
      contrastText: '#ffffff',
    },
    success: {
      main: colors.accent.green[500],
      light: colors.accent.green[400],
      dark: colors.accent.green[600],
      contrastText: '#ffffff',
      bg: colors.success.bg,
      border: colors.success.border,
    },
    error: {
      main: colors.accent.red[500],
      light: colors.accent.red[400],
      dark: colors.accent.red[600],
      contrastText: '#ffffff',
      bg: colors.error.bg,
      border: colors.error.border,
    },
    warning: {
      main: colors.accent.orange[500],
      light: colors.accent.orange[400],
      dark: colors.accent.orange[600],
      contrastText: '#ffffff',
      bg: colors.warning.bg,
      border: colors.warning.border,
    },
    info: {
      main: colors.accent.blue[500],
      light: colors.accent.blue[400],
      dark: colors.accent.blue[600],
      contrastText: '#ffffff',
      bg: colors.info.bg,
      border: colors.info.border,
    },
    background: {
      default: colors.bg.primary.light,
      paper: colors.bg.secondary.light,
      subtle: colors.bg.subtle.light,
      hover: colors.bg.hover.light,
    },
    text: {
      primary: colors.text.primary.light,
      secondary: colors.text.secondary.light,
      disabled: colors.text.muted.light,
    },
    divider: colors.border.default.light,
    action: {
      hover: colors.bg.hover.light,
    },
  },
});

// Dark theme
export const darkTheme = createTheme({
  ...commonOptions,
  palette: {
    mode: 'dark',
    primary: {
      main: colors.accent.blue.dark,
      light: colors.accent.blue[300],
      dark: colors.accent.blue[600],
      contrastText: '#ffffff',
    },
    secondary: {
      main: colors.accent.purple.dark,
      light: colors.accent.purple[300],
      dark: colors.accent.purple[700],
      contrastText: '#ffffff',
    },
    success: {
      main: colors.accent.green.dark,
      light: colors.accent.green[300],
      dark: colors.accent.green[500],
      contrastText: '#ffffff',
      bg: colors.success.bg,
      border: colors.success.border,
    },
    error: {
      main: colors.accent.red.dark,
      light: colors.accent.red[300],
      dark: colors.accent.red[500],
      contrastText: '#ffffff',
      bg: colors.error.bg,
      border: colors.error.border,
    },
    warning: {
      main: colors.accent.orange.dark,
      light: colors.accent.orange[300],
      dark: colors.accent.orange[500],
      contrastText: '#ffffff',
      bg: colors.warning.bg,
      border: colors.warning.border,
    },
    info: {
      main: colors.accent.blue.dark,
      light: colors.accent.blue[300],
      dark: colors.accent.blue[500],
      contrastText: '#ffffff',
      bg: colors.info.bg,
      border: colors.info.border,
    },
    background: {
      default: colors.bg.primary.dark,
      paper: colors.bg.secondary.dark,
      subtle: colors.bg.subtle.dark,
      hover: colors.bg.hover.dark,
    },
    text: {
      primary: colors.text.primary.dark,
      secondary: colors.text.secondary.dark,
      disabled: colors.text.muted.dark,
    },
    divider: colors.border.default.dark,
    action: {
      hover: colors.bg.hover.dark,
    },
  },
});

// Export helper to get theme by mode
export const getTheme = (mode: 'light' | 'dark') => {
  return mode === 'dark' ? darkTheme : lightTheme;
};
